import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { UI_LOCALE_NAMES, UI_LOCALES, type UiLocale } from '@kinshipos/shared-types';

/**
 * The admin panel in the reader's language.
 *
 * `t('Save changes')` looks the English text up in the current language's
 * dictionary and returns it — or the English itself when the language has no
 * entry yet, so an untranslated string is never blank. The English source is
 * the key: no separate key file to keep in step, and a string the panel
 * grows tomorrow simply shows in English until it is translated.
 *
 * The dictionaries are source, not data: one JSON file per language in
 * `./locales`, loaded lazily by `use-locale.ts`. A translation is reviewed in
 * a pull request and released with the panel, which is what a translation
 * needs — a native speaker reading it in context, not a text box in a
 * console. Placeholders are `{name}` or `{0}`.
 *
 * `t` is a plain function rather than a hook so a codemod could apply it to
 * a thousand strings in one pass; the shells re-key the page on the locale,
 * which is what makes an already-rendered screen change language.
 */

export { UI_LOCALE_NAMES, UI_LOCALES };
export type { UiLocale };

type Dictionary = Record<string, string>;

const state: { locale: UiLocale; dictionary: Dictionary } = { locale: 'en', dictionary: {} };

/** Applies a language's dictionary; the shells call this, then re-key the page. */
export function applyLocale(locale: UiLocale, dictionary: Dictionary): void {
  state.locale = locale;
  state.dictionary = dictionary;
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

export function currentLocale(): UiLocale {
  return state.locale;
}

type Var = string | number | null | undefined;
type Vars = Record<string, Var> | readonly Var[];

/** The current language's text for an English string, with `{placeholders}` filled in. */
export function t(text: string, vars?: Vars): string {
  const translated = state.dictionary[text] ?? text;
  if (!vars) return translated;
  return translated.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = Array.isArray(vars) ? (vars as readonly Var[])[Number(name)] : (vars as Record<string, Var>)[name];
    // A missing value renders as nothing, the way `${x}` did for an undefined x.
    return value === undefined || value === null ? '' : String(value);
  });
}

// ── The reader's choice ─────────────────────────────────────────────────────

interface LocaleStore {
  /** null = the platform's default language. */
  locale: UiLocale | null;
  setLocale: (locale: UiLocale | null) => void;
}

export const useLocaleStore = create<LocaleStore>()(
  persist((set) => ({ locale: null, setLocale: (locale) => set({ locale }) }), {
    name: 'sk:locale',
    storage: createJSONStorage(() => localStorage),
  }),
);

export function isUiLocale(value: unknown): value is UiLocale {
  return typeof value === 'string' && (UI_LOCALES as readonly string[]).includes(value);
}
